<template>

  <!-- NOV: First letter -->
  <LetterLayout v-if="first" :model="model" v-on="$attrs" hide-to :signature-margin-line-count="0">

    <template #from>
      <div class="position-relative">
        <img
          :src="logo_nov"
          width="144"
          class="seal-logo lg me-1 position-absolute top-0 end-0"
        />
        <div class="w-100 d-flex flex-column align-items-stretch gap-4 mb-5">
          <div class="text-start">
            <div class="fw-semibold small">City of Portland | Permitting and Inspections</div>
            <div>Licensing and Housing Safety Division</div>
            <div>Housingsafety@portlandmaine.gov | 207-756-8131</div>
          </div>
        </div>
      </div>
    </template>

    <template #subject>
      <div class="text-center fw-bold mb-3">
        NOTICE OF VIOLATION AND ORDER TO CORRECT
      </div>
    </template>

    <template #signature>
      Thank you for your cooperation.
      <br/>
      Sincerely,
      <br/>
      <img :src="signature" />
      <br/>
      Katlyn Sawyer Licensing & Registration Coordinator
    </template>

    <template #footer>
      389 Congress Street, Portland, Maine 04101 | 207-756-8131 | housingsafety@portlandmaine.gov
    </template>

    <template #default>
      <p class="fw-bold">
        Re: {{ model.addressFormatted }} (CBL {{ model.property.id }})
      </p>
      <p>
        Dear {{ model.ownerFormatted }}:
      </p>

      <template v-if="registered">
        <p>
          We are contacting you because your short-term rental advertisement is missing the required registration number, which is a violation of § 6-151(d) of the City of Portland Code of Ordinances. Please see the attached listing. The City of Portland requires that registration numbers must be displayed in the rental unit and in any and all advertisements for the rental unit.
        </p>
        <p>
          Your short-term rental registration number is: {{ licenseNumber }}
        </p>
        <p>
          Please include the registration in any and all advertisement of the unit.  If the advertising platform does not have a specific field for the registration number, please include it in the description of the unit.  This must be done within 14 days of the date of this letter.
        </p>
        <p>
          If you do not correct this violation within 14 days, then this matter will be referred to the City of Portland Corporation Counsel for legal action.  In the event of court action, the City may be entitled to an order to correct the violations, civil penalties in the minimum of $100 per violation per day, costs and fees, and other relief under §1-15 of the City Code and 30-A M.R.S. §4452.
        </p>
      </template>
      <template v-else>
        <p>
          We are contacting you because your property is being used as a short-term rental and is not registered with the City, which is a violation of § 6-151 of the City of Portland Code of Ordinances. Please see the attached listing. The City requires that all short-term rentals be registered with the City prior to being rented.
        </p>
        <p>
          Short-term rental units that are non-owner-occupied on the mainland and Peaks Island have caps established by § 6-153 of the City’s Code of Ordinances. Those caps are currently met and no registrations are available for those license types at this time. You may apply to be put on the waitlist for when registrations become available in the future. However, until your unit receives a registration, you must immediately stop using your property as a short-term rental, must cancel all future bookings, and must remove any short-term rental listings. This must be done within 14 days of the date of this letter.
        </p>
        <p>
          If the unit is eligible for an Owner-occupied, Peaks Island seasonal, or Outer Islands short-term rental registration, please complete an application and submit the required fee within 14 days of the date on this letter. You are welcome to register by mail and we will follow up with you. The application is available on our website at:  https://www.portlandmaine.gov/STR. Please contact the Housing Safety Office with questions about the eligibility of your unit.
        </p>
        <p>
          If you do not correct the attached violations by the date given, then this matter will be referred to the City of Portland Corporation Counsel for legal action.  In the event of court action, the City may be entitled to an order to correct the violations, civil penalties in the minimum of $100 per violation per day, costs and fees, and other relief under §1-15 of the City Code and 30-A M.R.S. §4452.
        </p>
      </template>

      <p class="fw-bold">
        You have a right to appeal this decision to Superior Court within 30 days of the date of this notice pursuant to Rule 80B of the Maine Rules of Civil Procedure. If you choose not to appeal this notice, then you may be barred from challenging the above findings and this decision in the future.
      </p>
      <p>
        If you have any questions, please contact us with the information below or stop by. Thank you.
      </p>
    </template>

  </LetterLayout>

  <!-- Intent to Prosecute: Second letter -->
  <LetterLayout v-else :model="model" v-on="$attrs" hide-to>
    <template #from>
      <div class="d-flex flex-row align-items-start gap-4 justify-content-between pb-4 mb-4 border-bottom border-dark">
        <div class="flex-grow-1">
          Office of Corporation Counsel<br/>
          Michael Goldman, <i>Corporation Counsel</i><br/>
          Amy R. McNally, <i>Associate Corporation Counsel</i><br/>
          Nicole M.  Albert, <i>Associate Corporation Counsel</i><br/>
          Rachel L. Millette, <i>Associate Corporation Counsel</i><br/>
          Avery A. Dandreta, <i>Associate Corporation Counsel</i>
        </div>
        <img
          :src="logo_noitp"
          height="84"
          class="seal-logo lg me-3"
        />
      </div>
    </template>

    <template #subject>
      <div class="text-center fw-bold mb-3">
        NOTICE OF INTENT TO PROSECUTE
      </div>
    </template>

    <template #signature>
      Sincerely,
      <br/><br/><br/><br/>
      Amy McNally<br/>
      Associate Corporation Counsel<br/>
      <a href="mailto:amcnally@portlandmaine.gov">amcnally@portlandmaine.gov</a> | 207-874-8974
    </template>

    <template #footer>
      389 Congress Street, Room 211 Portland, Maine 04101 | t: 207-874-8480
    </template>

    <template #default>
      <p class="fw-bold">
        Re: {{ model.addressFormatted }} (CBL {{ model.property.id }})
      </p>
      <p>
        Dear {{ model.ownerFormatted }}:
      </p>

      <template v-if="registered">
        <p>
          I am writing on behalf of the City of Portland regarding the Notice of Violations issued by the City’s Permitting and Inspections Department about the missing registration number in your online short term rental advertisement. This is a violation of § 6-151(d) of the City of Portland Code of Ordinances. Please note that Section 6-151(d) requires that registration numbers must be displayed in the rental unit and in any and all advertisements for the rental unit.
        </p>
        <p>
          Please include the short-term rental registration number in any and all advertisement of the unit.  If the advertising platform does not have a specific field for the registration number, please include it in the description of the unit.  This must be done within 14 days of the date of this letter.
        </p>
        <p>
          If you fail to display the registration number in your advertisements, the City will issue a summons for you to appear in court on the outstanding violations. In the event of court action, the City will request an order to correct the violations, civil penalties in the minimum of $100 per violation per day, costs and attorney’s fees, and other relief under §1-15 of the City Code and 30-A M.R.S. §4452.
        </p>
      </template>
      <template v-else>
        <p>
          I am writing on behalf of the City of Portland regarding the Notice of Violation issued by the City’s Permitting and Inspections Department about renting a unit at the above property on a short-term basis without having registered that unit with the City. You are operating an unregistered short-term rental, which is a violation of § 6-151 of the City of Portland Code of Ordinances.
        </p>
        <p>
          Short-term rental units that are non-owner-occupied on the mainland and Peak’s Island have caps established by § 6-153 of the City’s Code of Ordinances. Those caps are currently met and no registrations are available for those license types at this time. You may apply to be put on the waitlist for when registrations become available in the future. However, until your unit receives a registration, you must immediately stop using your property as a short-term rental, must cancel all future bookings, and must remove any short-term rental listings. This must be done within 14 days of the date of this letter. The City may be willing to give you some additional time to wind down your business in exchange for the payment of civil penalties, and you can contact me directly if you are interested in this option.
        </p>
        <p>
          If the unit is eligible for an Owner-occupied, Peaks Island seasonal, or Outer Islands short-term rental registration, please complete an application and submit the required fee within 14 days of the date on this letter. You are welcome to register by mail and we will follow up with you. The application is available on our website at:  https://www.portlandmaine.gov/STR. Please contact the Housing Safety Office with questions about the eligibility of your unit.
        </p>
        <p>
          If you fail to register in the time provided, or fail to remove your listing and cancel all bookings, the City will issue a summons for you to appear in court on the outstanding violations. In the event of court action, the City may be entitled to an order to correct the violations, civil penalties in the minimum of $100 per violation per day, costs and attorney’s fees, and other relief under §1-15 of the City Code and 30-A M.R.S. §4452.
        </p>
      </template>

      <p>
        If you require assistance registering or have further questions please contact the Licensing and Registration Office at 207-756-8131 or <a href="mailto:housingsafety@portlandmaine.gov">housingsafety@portlandmaine.gov</a>.
      </p>
      <p>
        Thank you for your anticipated cooperation.
      </p>

    </template>
  </LetterLayout>
</template>

<script>
import LetterLayout from "@/components/letters/LetterLayout.vue";
import {LetterContext} from "@/models/letterContext";
import signature from './portland_me/signature.png';
import logo_nov from './portland_me/logo_NOV.png';
import logo_noitp from './portland_me/logo_NOITP.png';

export default {
  components: {LetterLayout},
  props: {
    model: LetterContext,
    first: Boolean,
    registered: Boolean
  },
  data() {
    return {
      signature,
      logo_nov,
      logo_noitp
    };
  },
  computed: {
    licenseNumber() {
      return this.model.licenseNumber ?? null;
    }
  }
}
</script>
